import React, { useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Search, Hammer, ClipboardCheck, Rocket, Check, Store, RefreshCw, TrendingUp, ArrowRight } from 'lucide-react';
export function ShopifyPOS() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  return <div className="min-h-screen bg-[#FAF7F2] font-['Inter']" data-id="element-791">
      <Header data-id="element-792" />

      {/* Hero Section */}
      <section className="pt-28 pb-20 px-6 md:px-10" data-id="element-793">
        <div className="max-w-[1400px] mx-auto" data-id="element-794">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto" data-id="element-795">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-[#2C2416] text-[clamp(48px,6vw,72px)] font-light leading-[1.1] tracking-[-3px] mb-6" data-id="element-796">
              Launch Shopify POS in{' '}
              <span className="text-[#D4A574]" data-id="element-797">60 Days or Less</span>
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-[#8B7355] text-xl md:text-2xl mb-10 max-w-2xl leading-relaxed" data-id="element-798">
              Expert implementation for retail businesses ready to unify their
              in-store and online sales with Shopify's leading point-of-sale
              system.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-id="element-799">
              <a href="#contact" className="bg-[#2C2416] text-[#FAF7F2] px-9 py-[18px] rounded-full font-medium tracking-[0.5px] hover:scale-105 transition-all duration-300 hover:bg-[#D4A574]" data-id="element-800">
                Get Your Free Quote
              </a>
              <a href="#process" className="bg-transparent border-2 border-[#2C2416] text-[#2C2416] px-9 py-[18px] rounded-full font-medium tracking-[0.5px] hover:scale-105 transition-all duration-300 hover:bg-[#2C2416] hover:text-[#FAF7F2]" data-id="element-801">
                See Our Process
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-6 md:px-10 bg-white" data-id="element-802">
        <div className="max-w-[1400px] mx-auto" data-id="element-803">
          <div className="text-center mb-16" data-id="element-804">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-805">
              Our Proven 4-Phase Implementation Process
            </h2>
            <p className="text-[#8B7355] text-lg max-w-2xl mx-auto" data-id="element-806">
              A structured approach to ensure a smooth transition with minimal
              disruption to your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative" data-id="element-807">
            {/* Connecting Line (Desktop) */}
            <div className="hidden lg:block absolute top-12 left-0 w-full h-0.5 bg-[#D4A57420] -z-10" data-id="element-808"></div>

            {[{
            phase: 'Phase 1',
            title: 'EXPLORE',
            time: 'Week 1-2',
            icon: <Search className="w-8 h-8 text-[#D4A574]" data-id="element-809" />,
            items: ['Discovery call', 'Hardware assessment', 'Payment processor setup', 'Staff training plan']
          }, {
            phase: 'Phase 2',
            title: 'BUILD',
            time: 'Week 2-4',
            icon: <Hammer className="w-8 h-8 text-[#2C2416]" data-id="element-810" />,
            items: ['POS configuration', 'Product catalog setup', 'Inventory sync', 'Custom workflows']
          }, {
            phase: 'Phase 3',
            title: 'TEST',
            time: 'Week 4-5',
            icon: <ClipboardCheck className="w-8 h-8 text-[#D4A574]" data-id="element-811" />,
            items: ['Staff training sessions', 'Test transactions', 'Troubleshooting', 'Soft launch prep']
          }, {
            phase: 'Phase 4',
            title: 'GO-LIVE',
            time: 'Week 5-8',
            icon: <Rocket className="w-8 h-8 text-[#2C2416]" data-id="element-812" />,
            items: ['Full launch support', 'Real-time monitoring', 'Post-launch optimization', 'Ongoing support']
          }].map((step, index) => <motion.div key={index} {...fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-white p-8 rounded-xl border border-[#D4A57420] shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1" data-id="element-813">
                <div className="w-24 h-24 bg-[#FAF7F2] rounded-full flex items-center justify-center mb-6 mx-auto border-4 border-white shadow-sm" data-id="element-814">
                  {step.icon}
                </div>
                <div className="text-center mb-6" data-id="element-815">
                  <span className="text-[#D4A574] font-semibold text-sm uppercase tracking-wider block mb-1" data-id="element-816">
                    {step.phase}
                  </span>
                  <h3 className="text-2xl font-medium text-[#2C2416] mb-1" data-id="element-817">
                    {step.title}
                  </h3>
                  <span className="text-[#8B7355] font-medium" data-id="element-818">
                    {step.time}
                  </span>
                </div>
                <ul className="space-y-3" data-id="element-819">
                  {step.items.map((item, i) => <li key={i} className="flex items-start text-[#8B7355] text-sm" data-id="element-820">
                      <Check className="w-4 h-4 text-[#D4A574] mr-2 mt-0.5 flex-shrink-0" data-id="element-821" />
                      {item}
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-24 px-6 md:px-10 bg-[#FAF7F2]" data-id="element-822">
        <div className="max-w-[1400px] mx-auto" data-id="element-823">
          <div className="text-center mb-16" data-id="element-824">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-825">
              Is This Right For Your Business?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-826">
            {[{
            title: 'Retailers Going Omnichannel',
            icon: <Store className="w-10 h-10 text-[#D4A574]" data-id="element-827" />,
            desc: 'Currently online-only wanting physical presence. Need unified inventory and customer data.'
          }, {
            title: 'Existing Stores Upgrading',
            icon: <RefreshCw className="w-10 h-10 text-[#2C2416]" data-id="element-828" />,
            desc: 'Replacing outdated POS systems. Migrating from Square, Lightspeed, or legacy systems.'
          }, {
            title: 'Multi-Location Expansion',
            icon: <TrendingUp className="w-10 h-10 text-[#D4A574]" data-id="element-829" />,
            desc: 'Growing from 1 to multiple stores. Need centralized management and reporting.'
          }].map((item, index) => <motion.div key={index} {...fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-white p-10 rounded-xl border border-[#D4A57420] shadow-lg text-center hover:-translate-y-1 transition-all duration-300" data-id="element-830">
                <div className="w-20 h-20 bg-[#FAF7F2] rounded-2xl flex items-center justify-center mb-6 mx-auto" data-id="element-831">
                  {item.icon}
                </div>
                <h3 className="text-xl font-medium text-[#2C2416] mb-4" data-id="element-832">
                  {item.title}
                </h3>
                <p className="text-[#8B7355] leading-relaxed" data-id="element-833">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 md:px-10 bg-white" data-id="element-834">
        <div className="max-w-[1400px] mx-auto" data-id="element-835">
          <div className="text-center mb-16" data-id="element-836">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-837">
              Transparent Pricing for Every Business Size
            </h2>
            <p className="text-[#8B7355] text-lg" data-id="element-838">
              Choose the package that matches your retail footprint
            </p>
          </div>

          <div className="flex flex-nowrap overflow-x-auto pb-8 gap-6 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 lg:gap-8 snap-x snap-mandatory" data-id="element-839">
            {[{
            name: 'STARTER',
            loc: '1 Location',
            price: '$1,699',
            sub: 'Perfect for single-store retailers',
            features: ['Full POS setup', 'Hardware guidance', 'Staff training (up to 5)', '5-day post-launch support'],
            cta: 'Get Started',
            highlight: false
          }, {
            name: 'GROWTH',
            loc: '2 Locations',
            price: '$2,899',
            sub: 'For expanding businesses',
            features: ['Everything in Starter', 'Multi-location sync', 'Centralized reporting', '14-day post-launch support'],
            cta: 'Get Started',
            highlight: false
          }, {
            name: 'MULTI-STORE',
            loc: '3 Locations',
            price: '$3,999',
            sub: 'Most Popular Choice',
            features: ['Everything in Growth', 'Advanced inventory', 'Custom workflows', '21-day post-launch support'],
            cta: 'Get Started',
            highlight: true
          }, {
            name: 'ENTERPRISE',
            loc: '4 Locations',
            price: '$4,999',
            sub: 'For established retailers',
            features: ['Everything in Multi-Store', 'Dedicated PM', 'Extended training', '30-day post-launch support'],
            cta: 'Get Started',
            highlight: false
          }].map((plan, index) => <motion.div key={index} {...fadeIn} transition={{
            delay: index * 0.1
          }} className={`min-w-[280px] md:min-w-0 snap-center flex flex-col p-6 rounded-xl border ${plan.highlight ? 'border-[#D4A574] shadow-xl scale-105 z-10 bg-white relative' : 'border-[#D4A57420] bg-white shadow-lg hover:shadow-xl'}`} data-id="element-840">
                {plan.highlight && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#D4A574] text-[#2C2416] px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-wide" data-id="element-841">
                    Most Popular
                  </div>}
                <div className="mb-6" data-id="element-842">
                  <h3 className="text-sm font-semibold text-[#8B7355] uppercase tracking-wider mb-2" data-id="element-843">
                    {plan.name}
                  </h3>
                  <div className="text-3xl font-light text-[#2C2416] mb-1 tracking-[-1px]" data-id="element-844">
                    {plan.price}
                  </div>
                  <div className="text-[#D4A574] font-medium mb-2" data-id="element-845">
                    {plan.loc}
                  </div>
                  <p className="text-xs text-[#8B7355]" data-id="element-846">{plan.sub}</p>
                </div>
                <ul className="space-y-3 mb-8 flex-grow" data-id="element-847">
                  {plan.features.map((feature, i) => <li key={i} className="flex items-start text-sm text-[#8B7355]" data-id="element-848">
                      <Check className={`w-4 h-4 mr-2 mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-[#D4A574]' : 'text-[#D4A574]'}`} data-id="element-849" />
                      {feature}
                    </li>)}
                </ul>
                <a href="#contact" className={`w-full py-3 rounded-full font-medium text-center transition-all duration-300 hover:scale-105 ${plan.highlight ? 'bg-[#D4A574] text-[#2C2416] hover:bg-[#2C2416] hover:text-[#FAF7F2]' : 'bg-[#2C2416] text-[#FAF7F2] hover:bg-[#D4A574] hover:text-[#2C2416]'}`} data-id="element-850">
                  {plan.cta}
                </a>
              </motion.div>)}
          </div>

          <div className="text-center mt-12" data-id="element-851">
            <p className="text-[#8B7355]" data-id="element-852">
              Enterprise 5+ locations?{' '}
              <a href="#contact" className="text-[#D4A574] font-medium hover:underline" data-id="element-853">
                Contact us for custom pricing
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-24 px-6 md:px-10 bg-[#2C2416] text-white" data-id="element-854">
        <div className="max-w-[1400px] mx-auto" data-id="element-855">
          <div className="text-center mb-16" data-id="element-856">
            <h2 className="text-[#FAF7F2] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-857">
              Everything You Need for a Successful Launch
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-858">
            <div className="space-y-4" data-id="element-859">
              {['Complete POS Pro configuration', 'Payment processor integration', 'Product catalog and inventory sync', 'Customer profile migration', 'Staff role and permission setup'].map((item, index) => <div key={index} className="flex items-center p-4 bg-white/5 rounded-lg border border-white/10" data-id="element-860">
                  <div className="w-8 h-8 rounded-full bg-[#D4A574]/20 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-861">
                    <Check className="w-5 h-5 text-[#D4A574]" data-id="element-862" />
                  </div>
                  <span className="text-[#FAF7F2]/90 font-medium" data-id="element-863">{item}</span>
                </div>)}
            </div>
            <div className="space-y-4" data-id="element-864">
              {['Hands-on staff training sessions', 'Custom receipt and workflow setup', 'Test transaction validation', 'Go-live day support', 'Post-launch support (varies by package)'].map((item, index) => <div key={index} className="flex items-center p-4 bg-white/5 rounded-lg border border-white/10" data-id="element-865">
                  <div className="w-8 h-8 rounded-full bg-[#D4A574]/20 flex items-center justify-center mr-4 flex-shrink-0" data-id="element-866">
                    <Check className="w-5 h-5 text-[#D4A574]" data-id="element-867" />
                  </div>
                  <span className="text-[#FAF7F2]/90 font-medium" data-id="element-868">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Add-ons Section */}
      <section className="py-20 px-6 md:px-10 bg-[#FAF7F2]" data-id="element-869">
        <div className="max-w-[1400px] mx-auto" data-id="element-870">
          <div className="text-center mb-12" data-id="element-871">
            <h2 className="text-[#2C2416] text-[clamp(32px,4vw,40px)] font-light tracking-[-2px] mb-4" data-id="element-872">
              Optional Add-Ons
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-873">
            {[{
            title: 'Hardware Setup',
            price: '$399/location',
            desc: 'Remote configuration of scanners, printers, card readers'
          }, {
            title: 'Historical Data Migration',
            price: '$499',
            desc: 'Import past sales and transaction records'
          }, {
            title: 'Extended Support',
            price: '$199/month',
            desc: 'Ongoing priority support'
          }, {
            title: 'Additional Training',
            price: '$199/session',
            desc: 'Extra staff training sessions'
          }].map((addon, index) => <div key={index} className="bg-white p-8 rounded-xl border border-[#D4A57420] shadow-lg flex flex-col items-center text-center" data-id="element-874">
                <h3 className="font-medium text-lg text-[#2C2416] mb-2" data-id="element-875">
                  {addon.title}
                </h3>
                <div className="text-[#D4A574] font-light text-2xl mb-2 tracking-[-1px]" data-id="element-876">
                  {addon.price}
                </div>
                <p className="text-[#8B7355] text-sm" data-id="element-877">{addon.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-20 px-6 md:px-10" data-id="element-878">
        <div className="max-w-[1400px] mx-auto" data-id="element-879">
          <div className="bg-[#2C2416] text-center p-8 md:p-16 lg:p-24 rounded-3xl" data-id="element-880">
            <h2 className="text-[#FAF7F2] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4 md:mb-6" data-id="element-881">
              Ready to Transform Your Retail Operations?
            </h2>
            <p className="text-[#D4A574] text-lg md:text-xl max-w-2xl mx-auto mb-8 md:mb-12 leading-relaxed" data-id="element-882">
              Get a free consultation and custom quote for your business
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-883">
              <a href="/contact" className="bg-[#D4A574] text-[#2C2416] px-9 py-[18px] rounded-full font-medium tracking-[0.5px] hover:scale-105 transition-all duration-300 hover:bg-[#FAF7F2]" data-id="element-884">
                Book Your Free Consultation
              </a>
            </div>
            <p className="mt-8 text-[#FAF7F2]/80 font-medium" data-id="element-885">
              Or call us:{' '}
              <span className="text-[#FAF7F2] underline decoration-[#FAF7F2]/50 underline-offset-4 hover:decoration-[#FAF7F2] transition-all cursor-pointer" data-id="element-886">
                1-800-SHOPIFY-POS
              </span>
            </p>
          </div>
        </div>
      </section>

      <Footer data-id="element-887" />
    </div>;
}